/**
 * ConfirmDialog - Unified Confirmation Dialog Component
 *
 * This component provides a standardized confirmation dialog for the entire platform,
 * with special support for deletion confirmations following UX best practices.
 *
 * Features:
 * - Consistent visual design across all confirmations
 * - Danger variant optimized for deletion confirmations
 * - Accessible with proper ARIA attributes
 * - Keyboard navigation support
 * - Focus trapping
 *
 * @example
 * // Deletion confirmation (recommended)
 * <ConfirmDialog
 *   title="Eliminar Usuario"
 *   message="¿Estás seguro de que deseas eliminar al usuario "Juan Pérez"? Esta acción no se puede deshacer."
 *   confirmText="Eliminar"
 *   confirmStyle="danger"
 *   onConfirm={handleDelete}
 *   onCancel={handleCancel}
 * />
 *
 * @example
 * // General confirmation
 * <ConfirmDialog
 *   title="Guardar Cambios"
 *   message="¿Deseas guardar los cambios realizados?"
 *   confirmText="Guardar"
 *   confirmStyle="primary"
 *   onConfirm={handleSave}
 *   onCancel={handleCancel}
 * />
 */

import React from 'react';
import { FiAlertTriangle, FiAlertCircle, FiCheckCircle, FiInfo } from 'react-icons/fi';
import { Modal, Button } from '../../design-system';

const ConfirmDialog = ({
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  confirmStyle = 'primary',
  onConfirm,
  onCancel,
}) => {
  // Map confirmStyle to button variants and icon styles
  const styleConfig = {
    danger: {
      variant: 'danger',
      icon: FiAlertTriangle,
      iconBgColor: 'bg-red-100',
      iconColor: 'text-red-600',
    },
    warning: {
      variant: 'warning',
      icon: FiAlertCircle,
      iconBgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
    },
    success: {
      variant: 'success',
      icon: FiCheckCircle,
      iconBgColor: 'bg-green-100',
      iconColor: 'text-green-600',
    },
    primary: {
      variant: 'primary',
      icon: FiInfo,
      iconBgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
  };

  const config = styleConfig[confirmStyle] || styleConfig.primary;
  const IconComponent = config.icon;

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      size="md"
      showCloseButton={false}
      closeOnBackdropClick={true}
    >
      <Modal.Body>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className={`flex items-center justify-center h-12 w-12 rounded-full ${config.iconBgColor}`}>
              <IconComponent
                className={`h-6 w-6 ${config.iconColor}`}
                aria-hidden="true"
              />
            </div>
          </div>
          <div className="flex-1 pt-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {title}
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {message}
            </p>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onCancel}
        >
          {cancelText}
        </Button>
        <Button
          variant={config.variant}
          onClick={onConfirm}
        >
          {confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDialog;